/**
 * Identity Authentication Helpers
 * Helper functions for determining API call parameters based on JWT token structure
 */

import { identityAuth } from './IdentityAuth';

/**
 * Get API call parameters based on JWT token access control
 * Uses the specific logic provided by the user
 * @returns {Object} API parameters object
 */
export const getApiCallParams = () => {
  const userInfo = identityAuth.getUserInfo();
  
  if (!userInfo || !userInfo.accessControl) {
    return {
      isMat: false,
      groupId: null,
      schoolId: null,
      apiType: 'none',
      reason: 'no_access_control',
      schools: []
    };
  }

  const { groups, standaloneSchoolIds } = userInfo.accessControl;
  
  // Check if user has groups
  if (groups && groups.length > 0) {
    const group = groups[0]; // Use first group
    const groupId = group.group_id;
    const allSchools = group.all_schools;
    const schoolIds = group.school_ids || [];
    
    // Case 1: all_schools = true -> Use group_id for API calls (GROUP APIs)
    if (allSchools === true) {
      return {
        isMat: true,
        groupId: groupId,
        schoolId: null,
        apiType: 'group',
        reason: 'all_schools_true',
        group: group,
        schools: schoolIds
      };
    }
    
    // Case 2: school_ids.length === 1 -> Use that single school_id for API calls (SCHOOL APIs)
    if (schoolIds.length === 1) {
      return {
        isMat: false,
        groupId: null,
        schoolId: schoolIds[0],
        apiType: 'school',
        reason: 'single_school_id',
        group: group,
        schools: schoolIds
      };
    }
    
    // Case 3: groupId exists and school_ids.length > 0 -> Use group_id for API calls (GROUP APIs)
    if (groupId && schoolIds.length > 0) {
      return {
        isMat: true,
        groupId: groupId,
        schoolId: null,
        apiType: 'group',
        reason: 'group_with_multiple_schools',
        group: group,
        schools: schoolIds
      };
    }
    
    // Case 4: groupId exists but no schools -> Use group_id for API calls (GROUP APIs)
    if (groupId) {
      return {
        isMat: true,
        groupId: groupId,
        schoolId: null,
        apiType: 'group',
        reason: 'group_without_schools',
        group: group,
        schools: schoolIds
      };
    }
  }
  
  // Case 5: groupId === null and standalone_school_ids.length > 0 -> Use school_id for API calls (SCHOOL APIs), isMAT = true
  if (standaloneSchoolIds && standaloneSchoolIds.length > 0) {
    return {
      isMat: false,
      groupId: null,
      schoolId: standaloneSchoolIds[0], // Use first school as primary
      apiType: 'school',
      reason: 'standalone_schools',
      schools: standaloneSchoolIds
    };
  }
  
  // Case 6: No valid access control data
  return {
    isMat: false,
    groupId: null,
    schoolId: null,
    apiType: 'none',
    reason: 'no_valid_access'
  };
};

/**
 * Get the appropriate API hook parameters for React Query
 * @returns {Object} Hook parameters
 */
export const getApiHookParams = () => {
  const params = getApiCallParams();
  return {
    // For group-based APIs
    groupId: params.groupId,
    groupEnabled: params.apiType === 'group' && !!params.groupId,
    
    // For school-based APIs  
    schoolId: params.schoolId,
    schoolEnabled: params.apiType === 'school' && !!params.schoolId,
    
    // General flags
    isMat: params.isMat,
    apiType: params.apiType,
    reason: params.reason,
    
    // Additional data
    group: params.group,
    schoolIds: params.schools
  };
};

/**
 * Check if user has access to a specific school
 * @param {number} schoolId - School ID to check
 * @returns {boolean} Whether user has access
 */
export const hasAccessToSchool = (schoolId) => {
  const userInfo = identityAuth.getUserInfo();
  
  if (!userInfo || !userInfo.accessControl) {
    return false;
  }
  
  const { groups, standaloneSchoolIds } = userInfo.accessControl;
  
  // Check standalone schools
  if (standaloneSchoolIds && standaloneSchoolIds.includes(schoolId)) {
    return true;
  }
  
  // Check groups
  if (groups && groups.length > 0) {
    return groups.some(group => {
      // If all_schools is true, user has access to all schools
      if (group.allSchools) {
        return true;
      }
      // Check specific school_ids
      return group.school_ids && group.school_ids.includes(schoolId);
    });
  }
  
  return false;
};

/**
 * Get all schools user has access to
 * @returns {Array} Array of school IDs
 */
export const getAllAccessibleSchools = () => {
  const userInfo = identityAuth.getUserInfo();
  
  if (!userInfo || !userInfo.accessControl) {
    return [];
  }
  
  const { groups, standaloneSchoolIds } = userInfo.accessControl;
  const accessibleSchools = new Set();
  
  // Add standalone schools
  if (standaloneSchoolIds) {
    standaloneSchoolIds.forEach(id => accessibleSchools.add(id));
  }
  
  // Add schools from groups
  if (groups && groups.length > 0) {
    groups.forEach(group => {
      if (group.school_ids) {
        group.school_ids.forEach(id => accessibleSchools.add(id));
      }
    });
  }
  
  return Array.from(accessibleSchools);
};

/**
 * Get the primary school ID for API calls
 * @returns {number|null} Primary school ID
 */
export const getPrimarySchoolId = () => {
  const params = getApiCallParams();
  return params.schoolId;
};

/**
 * Get the primary group ID for API calls
 * @returns {number|null} Primary group ID
 */
export const getPrimaryGroupId = () => {
  const params = getApiCallParams();
  return params.groupId;
};

/**
 * Check if user should use MAT (Multi-Academy Trust) APIs
 * @returns {boolean} Whether to use MAT APIs
 */
export const shouldUseMatApis = () => {
  const params = getApiCallParams();
  return params.isMat;
};

/**
 * Debug function to log current API decision
 */
export const debugApiDecision = () => {
  const params = getApiCallParams();
  const userInfo = identityAuth.getUserInfo();
  
  return params;
};
